import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../utils/safeToast';
import {
  ArrowLeft,
  MapPin,
  User,
  Mail,
  QrCode,
  Edit,
  Trash2,
  Download,
  Copy,
  ClipboardList,
  Loader2,
  ExternalLink,
  Sparkles,
  AlertTriangle,
  FileText,
  Printer,
  Save
} from 'lucide-react';

const API = "/api";
const FRONTEND_URL = window.location.origin;

const PropertyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [owners, setOwners] = useState([]);
  const [cleaningCompanies, setCleaningCompanies] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [welcomeDialogOpen, setWelcomeDialogOpen] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [welcomeText, setWelcomeText] = useState('');
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const printRef = useRef(null);

  const scanUrl = `${FRONTEND_URL}/scan/${id}`;
  const departureUrl = `${FRONTEND_URL}/departure/${id}`;

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [propertyRes, ownersRes, cleaningRes, tasksRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`),
        axios.get(`${API}/owners`),
        axios.get(`${API}/cleaning-companies`),
        axios.get(`${API}/tasks?property_id=${id}`)
      ]);
      setProperty(propertyRes.data);
      setOwners(ownersRes.data);
      setCleaningCompanies(cleaningRes.data);
      setTasks(tasksRes.data);
      setFormData(propertyRes.data);
      setWelcomeText(propertyRes.data.welcome_text || '');
    } catch (error) {
      toast.error('Fehler beim Laden des Objekts');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.owner_id) {
      toast.error('Bitte wählen Sie einen Eigentümer');
      return;
    }
    if (!formData.cleaning_company_id) {
      toast.error('Bitte wählen Sie eine Reinigungsfirma');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(`${API}/properties/${id}`, {
        name: formData.name,
        street: formData.street,
        city: formData.city,
        owner_id: formData.owner_id,
        cleaning_company_id: formData.cleaning_company_id,
        internal_rules: formData.internal_rules
      });
      toast.success('Objekt aktualisiert');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveWelcomeText = async () => {
    setSavingWelcome(true);
    try {
      await axios.put(`${API}/properties/${id}`, {
        welcome_text: welcomeText
      });
      toast.success('A4-Aushang-Text gespeichert');
      setProperty({ ...property, welcome_text: welcomeText });
      // Dialog automatisch schließen nach erfolgreichem Speichern
      setWelcomeDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingWelcome(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/properties/${id}`);
      toast.success('Objekt gelöscht');
      navigate('/properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const copyScanUrl = () => {
    navigator.clipboard.writeText(scanUrl);
    toast.success('QR-Code Link kopiert');
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-${property.name.replace(/\s+/g, '-')}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrint = () => {
    // Öffnet die HTML-Druckseite in neuem Fenster
    const printUrl = `/print/a4/${property.id}`;
    window.open(printUrl, '_blank', 'width=800,height=1000');
  };

  const renderWelcomeTextForPrint = (text) => {
    if (!text) return '';
    // Convert markdown-like formatting to HTML
    return text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^• /gm, '&bull; ')
      .replace(/\n/g, '<br/>');
  };

  // Parse text into sections based on placeholders
  // Unterstützte Platzhalter:
  // - {{qrCode}} - Beide QR-Codes zusammen (Problem melden + Abreise)
  // - {{qr_aufgaben}} - Nur QR-Code für "Problem melden"
  // - {{qr_abreise}} - Nur QR-Code für "Abreise mitteilen"
  // - {{reinigungsfirma}} - Kontaktdaten der Reinigungsfirma
  const parseWelcomeText = (text) => {
    if (!text) return { sections: [], hasQrPlaceholder: false, hasCleaningPlaceholder: false, hasQrAufgaben: false, hasQrAbreise: false };
    
    const hasQr = text.includes('{{qrCode}}');
    const hasQrAufgaben = text.includes('{{qr_aufgaben}}');
    const hasQrAbreise = text.includes('{{qr_abreise}}');
    const hasCleaning = text.includes('{{reinigungsfirma}}');
    
    // Split text by placeholders and track what each section is
    let sections = [];
    let remaining = text;
    
    // All placeholders with their lengths
    const placeholders = [
      { marker: '{{qrCode}}', type: 'qrCode', length: 10 },
      { marker: '{{qr_aufgaben}}', type: 'qr_aufgaben', length: 15 },
      { marker: '{{qr_abreise}}', type: 'qr_abreise', length: 14 },
      { marker: '{{reinigungsfirma}}', type: 'reinigungsfirma', length: 19 }
    ];
    
    // Process the text sequentially
    while (remaining.length > 0) {
      // Find the first occurring placeholder
      let firstIndex = -1;
      let firstPlaceholder = null;
      
      for (const ph of placeholders) {
        const idx = remaining.indexOf(ph.marker);
        if (idx !== -1 && (firstIndex === -1 || idx < firstIndex)) {
          firstIndex = idx;
          firstPlaceholder = ph;
        }
      }
      
      if (firstIndex === -1) {
        // No more placeholders, add remaining text
        if (remaining.trim()) {
          sections.push({ type: 'text', content: renderWelcomeTextForPrint(remaining) });
        }
        break;
      }
      
      // Add text before placeholder
      const beforeText = remaining.substring(0, firstIndex);
      if (beforeText.trim()) {
        sections.push({ type: 'text', content: renderWelcomeTextForPrint(beforeText) });
      }
      
      // Add placeholder marker
      sections.push({ type: firstPlaceholder.type });
      
      // Continue with remaining text
      remaining = remaining.substring(firstIndex + firstPlaceholder.length);
    }
    
    return { 
      sections, 
      hasQrPlaceholder: hasQr || hasQrAufgaben || hasQrAbreise, 
      hasCleaningPlaceholder: hasCleaning,
      hasQrAufgaben,
      hasQrAbreise,
      hasQrBoth: hasQr
    };
  };

  const getStatusBadge = (status) => {
    const styles = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-amber-100 text-amber-700',
      erledigt: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      neu: 'Neu',
      in_bearbeitung: 'In Bearbeitung',
      erledigt: 'Erledigt'
    };
    return <Badge className={`${styles[status]} border-0`}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="space-y-8" data-testid="property-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/properties')}
          className="p-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
              {property.name}
            </h1>
            {property.is_incomplete && (
              <Badge className="bg-amber-100 text-amber-700 border-0">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Unvollständig
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <MapPin className="w-4 h-4 text-stone-400" />
            <span className="text-stone-500">{property.street}, {property.city}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setEditDialogOpen(true)}
            className="rounded-full"
            data-testid="edit-property-btn"
          >
            <Edit className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Bearbeiten</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(true)}
            className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="delete-property-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Incomplete Warning */}
          {property.is_incomplete && (
            <Card className="bg-amber-50 border border-amber-200">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-amber-800">Zuordnungen unvollständig</h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Bitte ergänzen Sie die fehlenden Pflichtfelder (Eigentümer und/oder Reinigungsfirma).
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={() => setEditDialogOpen(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Jetzt ergänzen
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Owner Info */}
          <Card className="bg-white border border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Eigentümer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className={`font-medium ${property.owner_name ? 'text-slate-900' : 'text-amber-600'}`}>
                  {property.owner_name || 'Nicht zugewiesen'}
                </p>
                {property.owner_email && (
                  <div className="flex items-center gap-2 text-stone-500">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${property.owner_email}`} className="hover:text-slate-900">
                      {property.owner_email}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Cleaning Company Info */}
          <Card className="bg-white border border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-slate-600" />
                Reinigungsfirma
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className={`font-medium ${property.cleaning_company_name ? 'text-slate-900' : 'text-amber-600'}`}>
                  {property.cleaning_company_name || 'Nicht zugewiesen'}
                </p>
                {property.cleaning_company_email && (
                  <div className="flex items-center gap-2 text-stone-500">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${property.cleaning_company_email}`} className="hover:text-slate-900">
                      {property.cleaning_company_email}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* A4 Aushang / Begrüßung - NEW SECTION */}
          <Card className="bg-white border border-stone-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#0E4C92]" />
                A4 Aushang / Begrüßung
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setWelcomeDialogOpen(true)}
                  className="rounded-lg"
                  data-testid="edit-welcome-btn"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Text bearbeiten
                </Button>
                <Button
                  size="sm"
                  onClick={() => setPrintPreviewOpen(true)}
                  className="rounded-lg bg-[#0E4C92] hover:bg-[#0A3A70]"
                  data-testid="print-welcome-btn"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Drucken
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-stone-500 mb-3">
                Druckbares Begrüßungsblatt mit QR-Code für Gäste.
              </p>
              <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                <p className="text-sm text-stone-600 whitespace-pre-wrap line-clamp-6">
                  {welcomeText || 'Kein Text vorhanden - klicken Sie auf "Text bearbeiten"'}
                </p>
                {welcomeText && welcomeText.length > 300 && (
                  <p className="text-xs text-stone-400 mt-2">... (gekürzt)</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Internal Rules */}
          {property.internal_rules && (
            <Card className="bg-white border border-stone-200">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Interne Verhaltensregeln</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-stone-600 whitespace-pre-wrap">{property.internal_rules}</p>
              </CardContent>
            </Card>
          )}

          {/* Tasks */}
          <Card className="bg-white border border-stone-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-slate-600" />
                Aufgaben ({tasks.length})
              </CardTitle>
              <Link to={`/tasks?property=${id}`}>
                <Button variant="ghost" size="sm" className="text-slate-600">
                  Alle anzeigen
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <p className="text-stone-500 text-center py-4">Keine Aufgaben vorhanden</p>
              ) : (
                <div className="space-y-3">
                  {tasks.slice(0, 5).map((task) => (
                    <Link
                      key={task.id}
                      to={`/tasks/${task.id}`}
                      className="block p-3 rounded-lg border border-stone-200 hover:border-slate-300 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 truncate">{task.title}</span>
                        {getStatusBadge(task.status)}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* QR Code Section */}
        <div className="space-y-6">
          {/* QR-Code für Aufgaben */}
          <Card className="bg-white border border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <QrCode className="w-5 h-5 text-[#0E4C92]" />
                QR-Code für Aufgaben
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Scannen Sie diesen Code, um direkt eine Aufgabe für dieses Objekt zu erstellen.
              </p>
              <div
                className="bg-white p-4 rounded-xl border border-stone-200 flex items-center justify-center cursor-pointer hover:border-[#0E4C92] transition-all"
                onClick={() => setQrDialogOpen(true)}
                data-testid="qr-code-preview"
              >
                <QRCodeSVG
                  id="qr-code"
                  value={scanUrl}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-lg"
                  onClick={copyScanUrl}
                  data-testid="copy-qr-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Link kopieren
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 rounded-lg"
                  onClick={downloadQRCode}
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Herunterladen
                </Button>
              </div>
              <a
                href={scanUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-[#0E4C92] hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Scan-Seite öffnen
              </a>
            </CardContent>
          </Card>

          {/* QR-Code für Abreise */}
          <Card className="bg-white border border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <QrCode className="w-5 h-5 text-emerald-600" />
                QR-Code für Abreise
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Gäste können über diesen QR-Code ihre Abreisezeit melden. Die Information wird automatisch an die Reinigungsfirma gesendet.
              </p>
              <div
                className="bg-white p-4 rounded-xl border border-stone-200 flex items-center justify-center"
                data-testid="departure-qr-code-preview"
              >
                <QRCodeSVG
                  id="departure-qr-code"
                  value={departureUrl}
                  size={180}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-lg"
                  onClick={() => {
                    navigator.clipboard.writeText(departureUrl);
                    toast.success('Abreise-Link kopiert');
                  }}
                  data-testid="copy-departure-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Link kopieren
                </Button>
              </div>
              <a
                href={departureUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-emerald-600 hover:underline"
              >
                <ExternalLink className="w-4 h-4" />
                Abreise-Seite öffnen
              </a>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Objekt bearbeiten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Objektname *</Label>
              <Input
                id="edit-name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-stone-50"
                required
                data-testid="edit-property-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-street">Straße *</Label>
              <Input
                id="edit-street"
                value={formData.street || ''}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="bg-stone-50"
                required
                data-testid="edit-property-street"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-city">Ort *</Label>
              <Input
                id="edit-city"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="bg-stone-50"
                required
                data-testid="edit-property-city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-owner">Eigentümer *</Label>
              <Select
                value={formData.owner_id || ''}
                onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
              >
                <SelectTrigger className={`bg-stone-50 ${!formData.owner_id ? 'border-amber-300' : ''}`} data-testid="edit-property-owner">
                  <SelectValue placeholder="Eigentümer wählen" />
                </SelectTrigger>
                <SelectContent>
                  {owners.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id}>
                      {owner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cleaning">Reinigungsfirma *</Label>
              <Select
                value={formData.cleaning_company_id || ''}
                onValueChange={(value) => setFormData({ ...formData, cleaning_company_id: value })}
              >
                <SelectTrigger className={`bg-stone-50 ${!formData.cleaning_company_id ? 'border-amber-300' : ''}`} data-testid="edit-property-cleaning">
                  <SelectValue placeholder="Reinigungsfirma wählen" />
                </SelectTrigger>
                <SelectContent>
                  {cleaningCompanies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-rules">Interne Verhaltensregeln</Label>
              <Textarea
                id="edit-rules"
                value={formData.internal_rules || ''}
                onChange={(e) => setFormData({ ...formData, internal_rules: e.target.value })}
                className="bg-stone-50 min-h-[100px]"
                data-testid="edit-property-rules"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                disabled={submitting}
                data-testid="save-property-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Speichern
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Welcome Text Edit Dialog */}
      <Dialog open={welcomeDialogOpen} onOpenChange={setWelcomeDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#0E4C92]" />
              A4-Aushang bearbeiten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-500">
              Bearbeiten Sie hier den Begrüßungstext für dieses Objekt. Der Text erscheint auf dem druckbaren A4-Aushang für Gäste.
            </p>
            
            {/* Platzhalter-Hinweis */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Verfügbare Platzhalter:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white px-2 py-1 rounded border border-blue-100">
                  <code className="text-blue-700">{'{{qrCode}}'}</code>
                  <span className="text-blue-600 text-xs ml-1">→ Beide QR-Codes</span>
                </div>
                <div className="bg-white px-2 py-1 rounded border border-blue-100">
                  <code className="text-blue-700">{'{{reinigungsfirma}}'}</code>
                  <span className="text-blue-600 text-xs ml-1">→ Kontaktdaten</span>
                </div>
                <div className="bg-white px-2 py-1 rounded border border-blue-100">
                  <code className="text-blue-700">{'{{qr_aufgaben}}'}</code>
                  <span className="text-blue-600 text-xs ml-1">→ Nur Aufgaben-QR</span>
                </div>
                <div className="bg-white px-2 py-1 rounded border border-blue-100">
                  <code className="text-blue-700">{'{{qr_abreise}}'}</code>
                  <span className="text-blue-600 text-xs ml-1">→ Nur Abreise-QR</span>
                </div>
              </div>
            </div>

            {/* Text Editor */}
            <div className="space-y-2">
              <Label htmlFor="welcome-text">Begrüßungstext</Label>
              <Textarea
                id="welcome-text"
                value={welcomeText}
                onChange={(e) => setWelcomeText(e.target.value)}
                className={`bg-white border-stone-200 min-h-[250px] font-mono text-sm ${welcomeText.length > 2000 ? 'border-amber-500 border-2' : ''}`}
                placeholder="Geben Sie hier den Begrüßungstext ein..."
                data-testid="welcome-text-input"
              />
              {/* Textlängen-Anzeige und Warnung */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-stone-400">
                  Formatierung: **fett**, • für Aufzählungen, Leerzeilen für Absätze
                </p>
                <span className={`text-xs font-medium ${welcomeText.length > 2000 ? 'text-amber-600' : welcomeText.length > 1500 ? 'text-stone-500' : 'text-emerald-600'}`}>
                  {welcomeText.length} / ~2000 Zeichen
                </span>
              </div>
              {welcomeText.length > 2000 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Text möglicherweise zu lang</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Der Text könnte auf dem A4-Aushang nicht vollständig auf eine Seite passen. 
                      Bitte kürzen Sie den Text, um sicherzustellen, dass er auf einer Seite bleibt.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setWelcomeDialogOpen(false)}
                className="flex-1 rounded-full"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSaveWelcomeText}
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                disabled={savingWelcome}
                data-testid="save-welcome-btn"
              >
                {savingWelcome && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                <Save className="w-4 h-4 mr-2" />
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Preview Dialog */}
      <Dialog open={printPreviewOpen} onOpenChange={setPrintPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Printer className="w-5 h-5 text-[#0E4C92]" />
              Druckvorschau - A4 Aushang
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto" style={{ maxHeight: '80vh' }}>
            {/* Print Preview Container - DIN A4 optimiert mit Arial 11pt */}
            <div 
              ref={printRef}
              className="bg-white border border-stone-300 shadow-lg mx-auto"
              style={{ 
                width: '210mm', 
                minHeight: '297mm',
                maxHeight: '297mm',
                padding: '12mm 15mm', 
                overflow: 'hidden', 
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: '11pt',
                lineHeight: '1.4'
              }}
            >
              <div className="container" style={{ height: '100%', display: 'flex', flexDirection: 'column', maxHeight: '273mm' }}>
                {/* Header - kompakt */}
                <div className="header" style={{ textAlign: 'center', marginBottom: '4mm', paddingBottom: '3mm', borderBottom: '2px solid #0E4C92', flexShrink: 0 }}>
                  <img 
                    src="https://customer-assets.emergentagent.com/job_maintenance-hub-128/artifacts/ozn5f15c_LOGOneu.jpg" 
                    alt="GB Sylt Logo" 
                    style={{ width: '35mm', height: 'auto', margin: '0 auto 3mm', display: 'block' }}
                  />
                  <div className="title" style={{ fontSize: '14pt', fontWeight: 'bold', color: '#0E4C92', marginBottom: '1mm' }}>
                    Moin und willkommen!
                  </div>
                  <div className="subtitle" style={{ fontSize: '10pt', color: '#64748b' }}>
                    {property.name} • {property.street}, {property.city}
                  </div>
                </div>

                {/* Content - flexibel */}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  {/* Dynamic Content based on placeholders */}
                  {parseWelcomeText(welcomeText).sections.map((section, index) => {
                    if (section.type === 'text') {
                      return (
                        <div 
                          key={index}
                          className="content" 
                          style={{ marginBottom: '3mm', fontSize: '11pt', lineHeight: '1.4' }}
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      );
                    }
                    if (section.type === 'qrCode') {
                      // Beide QR-Codes zusammen
                      return (
                        <div key={index} className="qr-section" style={{ margin: '4mm 0', padding: '4mm', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', gap: '4mm' }}>
                            {/* QR-Code 1: Problem melden */}
                            <div style={{ textAlign: 'center', flex: 1 }}>
                              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                                Problem melden
                              </div>
                              <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                                <QRCodeSVG
                                  value={scanUrl}
                                  size={60}
                                  level="H"
                                  includeMargin={false}
                                />
                              </div>
                            </div>
                            {/* QR-Code 2: Abreise mitteilen */}
                            <div style={{ textAlign: 'center', flex: 1 }}>
                              <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                                Abreise mitteilen
                              </div>
                              <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                                <QRCodeSVG
                                  value={departureUrl}
                                  size={60}
                                  level="H"
                                  includeMargin={false}
                                />
                              </div>
                            </div>
                          </div>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10pt', color: '#64748b', textAlign: 'center', marginTop: '2mm' }}>
                            Scannen Sie den QR-Code mit Ihrem Smartphone
                          </div>
                        </div>
                      );
                    }
                    if (section.type === 'qr_aufgaben') {
                      // Nur QR-Code für "Problem melden / Aufgaben"
                      return (
                        <div key={index} className="qr-section" style={{ margin: '4mm 0', padding: '4mm', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                              Problem melden
                            </div>
                            <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                              <QRCodeSVG
                                value={scanUrl}
                                size={70}
                                level="H"
                                includeMargin={false}
                              />
                            </div>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10pt', color: '#64748b', marginTop: '2mm' }}>
                              Scannen Sie den QR-Code mit Ihrem Smartphone
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (section.type === 'qr_abreise') {
                      // Nur QR-Code für "Abreise mitteilen"
                      return (
                        <div key={index} className="qr-section" style={{ margin: '4mm 0', padding: '4mm', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                              Abreise mitteilen
                            </div>
                            <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                              <QRCodeSVG
                                value={departureUrl}
                                size={70}
                                level="H"
                                includeMargin={false}
                              />
                            </div>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10pt', color: '#64748b', marginTop: '2mm' }}>
                              Scannen Sie den QR-Code mit Ihrem Smartphone
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (section.type === 'reinigungsfirma') {
                      return (
                        <div key={index} className="contact-section" style={{ margin: '4mm 0', padding: '3mm', background: '#f0f9ff', border: '1px solid #0E4C92', borderRadius: '2mm' }}>
                          <div style={{ fontSize: '11pt', fontWeight: '600', color: '#0E4C92', marginBottom: '1mm' }}>
                            Ihre Reinigungsfirma:
                          </div>
                          {property.cleaning_company_name ? (
                            <>
                              <div style={{ fontSize: '11pt', color: '#1e293b' }}>
                                <strong>{property.cleaning_company_name}</strong>
                              </div>
                              {property.cleaning_company_phone && (
                                <div style={{ fontSize: '10pt', color: '#64748b' }}>
                                  Tel: {property.cleaning_company_phone}
                                </div>
                              )}
                              {property.cleaning_company_email && (
                                <div style={{ fontSize: '10pt', color: '#64748b' }}>
                                  E-Mail: {property.cleaning_company_email}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontSize: '10pt', color: '#94a3b8', fontStyle: 'italic' }}>
                              Keine Reinigungsfirma zugewiesen
                            </div>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Fallback: Show QR codes at end if no placeholder */}
                  {!parseWelcomeText(welcomeText).hasQrPlaceholder && (
                    <div className="qr-section" style={{ margin: '4mm 0', padding: '4mm', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '2mm' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', gap: '4mm' }}>
                        {/* QR-Code 1: Problem melden */}
                        <div style={{ textAlign: 'center', flex: 1 }}>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                            Problem melden
                          </div>
                          <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                            <QRCodeSVG
                              value={scanUrl}
                              size={60}
                              level="H"
                              includeMargin={false}
                            />
                          </div>
                        </div>
                        {/* QR-Code 2: Abreise mitteilen */}
                        <div style={{ textAlign: 'center', flex: 1 }}>
                          <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '12pt', fontWeight: '600', color: '#0E4C92', marginBottom: '2mm' }}>
                            Abreise mitteilen
                          </div>
                          <div style={{ margin: '2mm auto', display: 'flex', justifyContent: 'center' }}>
                            <QRCodeSVG
                              value={departureUrl}
                              size={60}
                              level="H"
                              includeMargin={false}
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10pt', color: '#64748b', textAlign: 'center', marginTop: '2mm' }}>
                        Scannen Sie den QR-Code mit Ihrem Smartphone
                      </div>
                    </div>
                  )}

                  {/* Fallback: Show cleaning company at end if no placeholder */}
                  {!parseWelcomeText(welcomeText).hasCleaningPlaceholder && (
                    <div className="contact-section" style={{ margin: '3mm 0', padding: '3mm', background: '#f0f9ff', border: '1px solid #0E4C92', borderRadius: '2mm' }}>
                      <div style={{ fontSize: '3mm', fontWeight: '600', color: '#0E4C92', marginBottom: '1mm' }}>
                        Ihre Reinigungsfirma:
                      </div>
                      {property.cleaning_company_name ? (
                        <>
                          <div style={{ fontSize: '3mm', color: '#1e293b' }}>
                            <strong>{property.cleaning_company_name}</strong>
                          </div>
                          {property.cleaning_company_phone && (
                            <div style={{ fontSize: '2.8mm', color: '#64748b' }}>
                              Tel: {property.cleaning_company_phone}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: '2.8mm', color: '#94a3b8', fontStyle: 'italic' }}>
                          Keine Reinigungsfirma zugewiesen
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Footer - fixiert */}
                <div className="footer" style={{ marginTop: 'auto', paddingTop: '2mm', borderTop: '1px solid #e2e8f0', textAlign: 'center', fontSize: '2.2mm', color: '#94a3b8', flexShrink: 0 }}>
                  GB Sylt • Objekt- & Aufgabenmanagement
                </div>
              </div>
            </div>

            {/* Print Buttons */}
            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPrintPreviewOpen(false)}
                className="flex-1 rounded-full"
              >
                Schließen
              </Button>
              <Button
                onClick={handlePrint}
                className="flex-1 bg-[#0E4C92] hover:bg-[#0A3A70] rounded-full"
                data-testid="confirm-print-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Jetzt drucken
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Full Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">QR-Code für {property.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <QRCodeSVG
              value={scanUrl}
              size={280}
              level="H"
              includeMargin={true}
            />
            <p className="mt-4 text-sm text-slate-500 text-center">
              Scannen Sie diesen Code, um eine neue Aufgabe zu erstellen
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Mitarbeiter werden nach Anmeldung gefragt, Gäste können ohne Login melden
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Objekt löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie &quot;{property.name}&quot; wirklich löschen? Alle zugehörigen Aufgaben werden ebenfalls gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              data-testid="confirm-delete-btn"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PropertyDetailPage;
